import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { Plus, Calendar as CalendarIcon, Clock, Eye, Loader2 } from 'lucide-react';
import { NewTaskModal } from '@/components/Events/NewTaskModal';
import { TaskDetailModal } from '@/components/Events/TaskDetailModal';
import { eventService } from '@/services/eventService';
import { useEventPermission } from '@/hooks/useEventPermission';
import { EventTask } from '@/types';

export const EventCalendar: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [showNewTaskModal, setShowNewTaskModal] = useState(false);
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'agenda' | 'full'>('agenda');
  const { canWrite } = useEventPermission(eventId);

  const { data: event } = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => (eventId ? eventService.getEvent(eventId) : Promise.resolve(null)),
    enabled: !!eventId,
  });

  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ['eventTasks', eventId],
    queryFn: () => (eventId ? eventService.getEventTasks(eventId) : Promise.resolve([])),
    enabled: !!eventId,
  });

  // Event providers — needed for both modals' provider picker and for the
  // task chips. Cached separately so both the New and Detail flows reuse it.
  const { data: eventProviders = [] } = useQuery({
    queryKey: ['eventProviders', eventId],
    queryFn: () => (eventId ? eventService.getEventProviders(eventId) : Promise.resolve([])),
    enabled: !!eventId,
  });

  const invalidateTasks = () => {
    queryClient.invalidateQueries({ queryKey: ['eventTasks', eventId] });
  };

  const selectedTask = tasks.find((t) => t.id === selectedTaskId) ?? null;

  const formatEventDate = (date: Date | undefined) => {
    if (!date) return '';
    return new Intl.DateTimeFormat('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  const getTasksForDate = (date: Date) =>
    tasks.filter((t) => t.inicio.toDateString() === date.toDateString());

  const formatTime = (date: Date) =>
    date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

  const formatDate = (date: Date) =>
    date.toLocaleDateString('es-ES', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const renderProviderChips = (task: EventTask) => {
    const providers = task.providers ?? [];
    if (providers.length === 0) return null;
    const shown = providers.slice(0, 3);
    const overflow = providers.length - shown.length;
    return (
      <div className="flex flex-wrap gap-1 mt-1">
        {shown.map((p) => (
          <Badge key={p.evento_proveedor_id} variant="secondary" className="text-xs">
            {p.tipo ? `${p.tipo}: ` : ''}{p.nombre}
          </Badge>
        ))}
        {overflow > 0 && (
          <Badge variant="outline" className="text-xs">+{overflow} más</Badge>
        )}
      </div>
    );
  };

  const renderTaskRow = (task: EventTask, showFullDate: boolean) => (
    <button
      key={task.id}
      type="button"
      onClick={() => setSelectedTaskId(task.id)}
      className="w-full text-left border-l-4 border-l-blue-500 pl-4 py-2 hover:bg-blue-50/50 rounded-r transition-colors"
    >
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0 flex-1">
          <h4 className="font-semibold text-gray-900 truncate">{task.titulo}</h4>
          <p className="text-sm text-gray-600">
            {showFullDate ? `${formatDate(task.inicio)} - ${formatTime(task.inicio)}` : formatTime(task.inicio)}
          </p>
          {renderProviderChips(task)}
        </div>
      </div>
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Cronograma del Evento</h1>
          <p className="text-gray-600">
            {event?.nombre || 'Cargando...'}
            {event?.fecha ? ` - ${formatEventDate(event.fecha)}` : ''}
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <Button
            variant={viewMode === 'full' ? 'default' : 'outline'}
            onClick={() => setViewMode(viewMode === 'full' ? 'agenda' : 'full')}
            className="flex items-center"
          >
            <Eye className="w-4 h-4 mr-2" />
            {viewMode === 'full' ? 'Vista Agenda' : 'Calendario Completo'}
          </Button>
          <Button
            onClick={() => setShowNewTaskModal(true)}
            disabled={!canWrite}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nueva Tarea
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendario */}
        <Card className={viewMode === 'full' ? 'lg:col-span-2' : ''}>
          <CardHeader>
            <CardTitle className="flex items-center">
              <CalendarIcon className="w-5 h-5 mr-2" />
              {viewMode === 'full' ? 'Calendario Completo' : 'Seleccionar Fecha'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-md border"
              modifiers={{
                booked: tasks.map((t) => t.inicio),
              }}
              modifiersStyles={{
                booked: {
                  backgroundColor: '#3B82F6',
                  color: 'white',
                  fontWeight: 'bold',
                },
              }}
            />
            {viewMode === 'agenda' && (
              <div className="mt-4 text-sm text-gray-600">
                <p className="font-semibold">Fecha seleccionada:</p>
                <p>{formatDate(selectedDate)}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tareas del día / Lista completa */}
        <Card className={viewMode === 'full' ? 'lg:col-span-1' : 'lg:col-span-2'}>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              {viewMode === 'full'
                ? 'Próximas Tareas'
                : `Tareas del ${selectedDate.toLocaleDateString('es-ES')}`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {tasksLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                </div>
              ) : viewMode === 'full' ? (
                tasks.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Aún no hay tareas programadas</p>
                  </div>
                ) : (
                  [...tasks]
                    .sort((a, b) => a.inicio.getTime() - b.inicio.getTime())
                    .map((task) => renderTaskRow(task, true))
                )
              ) : getTasksForDate(selectedDate).length > 0 ? (
                getTasksForDate(selectedDate).map((task) => renderTaskRow(task, false))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CalendarIcon className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No hay tareas programadas para este día</p>
                  {canWrite && (
                    <p className="text-sm">Agrega una nueva tarea usando el botón de arriba</p>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {eventId && (
        <NewTaskModal
          open={showNewTaskModal}
          onOpenChange={setShowNewTaskModal}
          eventId={eventId}
          eventProviders={eventProviders}
          onCreated={invalidateTasks}
        />
      )}

      <TaskDetailModal
        open={!!selectedTaskId}
        onOpenChange={(o) => !o && setSelectedTaskId(null)}
        task={selectedTask}
        eventProviders={eventProviders}
        canWrite={canWrite}
        onMutated={invalidateTasks}
      />
    </div>
  );
};
